import React, { FC, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ExpandMore, UnfoldMore, DisabledByDefaultRounded } from '@mui/icons-material';
import { TableContainer, Table, TableHead, TableRow, TableBody, TableCell, Paper, Checkbox } from '@mui/material';
import Loading from 'components/Loading/Loading';
import { hasWildfirePermissionsSelector } from 'helpers/auth0';
import { formatLocalDateTime } from 'helpers/DateHelpers';
import { BoostedOffer, BoostedOfferSortBy, CampaignsData, CampaignsType } from 'reduxState/store/boostedOffers/types';
import { FUNDING_SOURCE_LABELS } from '../constants';
import { tableHeaders } from './constants';
import TableSortLabel from '../../TableSortLabel/TableSortLabel';

interface BoostedOffersTableProps {
  campaignsData: CampaignsData;
  campaignsType: CampaignsType;
  error: string | Error;
  isLoading: boolean;
  isDeleteMode: boolean;
  isBatchEditMode: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  sortTable: (header: BoostedOfferSortBy) => void;
  toggleCampaignToDelete: (campaign: BoostedOffer, shouldBeDeleted: boolean) => void;
  toggleCampaignToEdit: (campaign: BoostedOffer, shouldBeEdited: boolean) => void;
  campaignsToEdit: BoostedOffer[];
  setCampaignToEdit: (campaign: BoostedOffer | null) => void;
  setIsEditingActiveCampaign: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectAll?: (shouldSelect: boolean) => void;
}

const BoostedOffersTable: FC<React.PropsWithChildren<BoostedOffersTableProps>> = ({
  campaignsData,
  campaignsType,
  error,
  isLoading,
  isDeleteMode,
  isBatchEditMode,
  sortBy,
  sortOrder,
  sortTable,
  toggleCampaignToDelete,
  toggleCampaignToEdit,
  campaignsToEdit,
  setCampaignToEdit,
  setIsEditingActiveCampaign,
  onSelectAll,
}) => {
  const [hoveredCampaignId, setHoveredCampaignId] = useState('');
  const { user } = useAuth0();
  const hasWfPermissions = hasWildfirePermissionsSelector(user);

  const canEditCampaign = hasWfPermissions && !isDeleteMode && !isBatchEditMode && campaignsType !== 'past';
  
  const currentCampaigns = campaignsData[campaignsType];
  const allSelected = isBatchEditMode && currentCampaigns.length > 0 && currentCampaigns.every(campaign => 
    campaignsToEdit.some(selected => selected.ID === campaign.ID)
  );
  const someSelected = isBatchEditMode && currentCampaigns.some(campaign => 
    campaignsToEdit.some(selected => selected.ID === campaign.ID)
  );

  const renderHeader = () => {
    const headerCells: JSX.Element[] = [];
    
    // Add select all checkbox at the beginning if in batch edit mode
    if (isBatchEditMode) {
      headerCells.push(
        <TableCell key="select-all">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e) => onSelectAll?.(e.target.checked)}
          />
        </TableCell>
      );
    }

    tableHeaders.forEach((header, index) => {
      const isActive = sortBy === header.key;
      switch (header.key) {
        case 'Delete':
          if (isDeleteMode) {
            headerCells.push(<TableCell key={index.toString()}>Delete</TableCell>);
          }
          break;
        case 'ApplicationID':
        case 'StartDateTime':
        case 'EndDateTime':
          headerCells.push(
            <TableCell key={index.toString()}>
              <TableSortLabel
                active={isActive}
                direction={sortOrder}
                onClick={(): void => sortTable(header.apiAlias as BoostedOfferSortBy)}
                IconComponent={isActive ? ExpandMore : UnfoldMore}
              >
                {header.value}
              </TableSortLabel>
            </TableCell>
          );
          break;
        default:
          headerCells.push(<TableCell key={index.toString()}>{header.value}</TableCell>);
          break;
      }
    });

    return headerCells;
  };

  const editCampaign = (campaign: BoostedOffer) => {
    if (campaignsType === 'future') {
      setCampaignToEdit(campaign);
      setIsEditingActiveCampaign(false);
    }

    if (campaignsType === 'present') {
      setCampaignToEdit(campaign);
      setIsEditingActiveCampaign(true);
    }
  };
  const renderBody = () =>
    campaignsData[campaignsType].map((campaign, index) => {
      const msBeforeStart = new Date(campaign.StartDate).getTime() - Date.now();
      const startWithinThreeHrs = msBeforeStart <= 1000 * 60 * 60 * 3;
      const isRatePercentage = campaign.Kind === 'PERCENTAGE';
      const isSelected = campaignsToEdit.some(selected => selected.ID === campaign.ID);
      const msBeforeEnd = new Date(campaign.EndDate).getTime() - Date.now();
      const endsWithinThreeHrs = Math.abs(msBeforeEnd) < 1000 * 60 * 60 * 3;

      return (
        <TableRow
          onMouseEnter={() => setHoveredCampaignId(campaign.ID.toString())}
          onMouseLeave={() => setHoveredCampaignId('')}
          hover={hoveredCampaignId === campaign.ID.toString()}
          key={campaign.ID.toString()}
          onClick={canEditCampaign ? () => editCampaign(campaign) : undefined}
          style={canEditCampaign ? { cursor: 'pointer' } : undefined}
        >
          {isDeleteMode && (
            <TableCell className="overflow-visible font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap">
              {startWithinThreeHrs ? (
                <div className="flex items-center justify-center overflow-visible">
                  <span className="flex items-center justify-center overflow-visible tooltip">
                    <DisabledByDefaultRounded color="error" />
                    <span className="flex items-center justify-center overflow-visible tooltip-text">
                      Campaigns starting within three hours cannot be deleted
                    </span>
                  </span>
                </div>
              ) : (
                <Checkbox
                  onChange={e => toggleCampaignToDelete(campaign, e.target.checked)}
                  disabled={startWithinThreeHrs}
                  title={startWithinThreeHrs ? 'Campaigns starting within three hours cannot be deleted' : undefined}
                />
              )}
            </TableCell>
          )}
          {isBatchEditMode && (
            <TableCell className="overflow-visible font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap">
              {campaignsType === 'present' && endsWithinThreeHrs ? (
                <div className="flex items-center justify-center overflow-visible">
                  <span className="flex items-center justify-center overflow-visible tooltip">
                    <DisabledByDefaultRounded color="error" />
                    <span className="flex items-center justify-center overflow-visible tooltip-text">
                      Campaigns ending within three hours cannot be edited
                    </span>
                  </span>
                </div>
              ) : (
                <Checkbox
                  checked={isSelected}
                  onChange={e => toggleCampaignToEdit(campaign, e.target.checked)}
                  disabled={campaignsType === 'present' && endsWithinThreeHrs}
                  title={campaignsType === 'present' && endsWithinThreeHrs ? 'Campaigns ending within three hours cannot be edited' : undefined}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </TableCell>
          )}
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {campaign.ApplicationName}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {campaign.ApplicationID}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {campaign.MerchantName}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {campaign.MerchantID}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {formatLocalDateTime(campaign.StartDate, 'M/dd/yyyy haaa')}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {formatLocalDateTime(campaign.EndDate, 'M/dd/yyyy haaa')}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {isRatePercentage ? `${campaign.Rate}% [Percentage]` : `${campaign.Rate}x [Multiplier]`}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {FUNDING_SOURCE_LABELS[campaign.FundingSource]}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] overflow-hidden text-ellipsis p-3 whitespace-nowrap">
            {formatLocalDateTime(campaign.CreatedDate, 'M/dd/yyyy')}
          </TableCell>
        </TableRow>
      );
    });

  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <TableContainer className="table-container mt-[30px] max-h-[400px] w-full max-w-[1600px] overflow-auto text-center rounded-md shadow-lg">
        {error ? (
          <Paper style={{ padding: '10px' }}>
            <p>An error occurred. Please try again in a few minutes.</p>
            <p>{error instanceof Error ? error.message : error}</p>
          </Paper>
        ) : isLoading && !campaignsData[campaignsType].length ? ( // only show loading spinner if there is currently no campaigns to show, so that the table doesn't flash every time it is updated
          <Paper style={{ padding: '10px' }}>
            <Loading data-testid="loading" />
          </Paper>
        ) : !campaignsData[campaignsType].length ? (
          <Paper style={{ padding: '10px' }}>
            <p>{`There are no ${campaignsType} campaigns for the selected application(s). Try again.`}</p>
          </Paper>
        ) : (
          <Table className="bg-white">
            <TableHead
              className="sticky top-0 z-10 table-header-group bg-light-grey"
              sx={{
                '& .MuiTableCell-root': {
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: 'var(--dark-grey)',
                  textWrap: 'nowrap',
                  textTransform: 'uppercase',
                  padding: '10px',
                  borderRight: '1px solid var(--dark-grey)',
                  backgroundColor: 'var(--light-grey)',

                  '&:last-of-type': {
                    border: 'none',
                  },
                },
              }}
            >
              <TableRow>{renderHeader()}</TableRow>
            </TableHead>
            <TableBody>{renderBody()}</TableBody>
          </Table>
        )}
      </TableContainer>
    </div>
  );
};

export default BoostedOffersTable;
